/**
 * On-site installation & deployment checklist, transcribed from the TechForce
 * "Installation Check List" sheet. Used by installation appointments; the order
 * page keeps its own pre-sale checklist in src/data/salesChecklist.ts.
 *
 * Entry kinds: `checkbox` = plain check; `field` = a value that must be typed in
 * (serial numbers, IP addresses); `photo` = camera/upload attachments. Anything to
 * say about a section goes through the appointment's notes, not the checklist.
 *
 * The sheet's first row, "Call and Schedule Installation Date", is not repeated
 * here — the appointment record itself is that scheduling step.
 */

export type InstallationChecklistEntry = 'checkbox' | 'field' | 'photo';

export type InstallationChecklistItem = {
  id: string;
  label: string;
  entry: InstallationChecklistEntry;
  /** Only for entry === 'field'. */
  placeholder?: string;
};

export type InstallationChecklistSection = {
  id: string;
  title: string;
  /** Verbatim from the sheet when its row enumerates what to verify. */
  description?: string;
  items: InstallationChecklistItem[];
};

export const INSTALLATION_CHECKLIST_SECTIONS: InstallationChecklistSection[] = [
  {
    id: 'install-verify-items',
    title: '1. Verify All Needed Installation Items',
    description: 'Robot / Bins / Bases / CR202 / Tools / Door Openers / Elevator Relays',
    items: [
      { id: 'verify-robot', label: 'Robot', entry: 'checkbox' },
      { id: 'verify-bins', label: 'Bins', entry: 'checkbox' },
      { id: 'verify-bases', label: 'Bases', entry: 'checkbox' },
      { id: 'verify-cr202', label: 'CR202', entry: 'checkbox' },
      { id: 'verify-tools', label: 'Tools', entry: 'checkbox' },
      { id: 'verify-door-openers', label: 'Door Openers', entry: 'checkbox' },
      { id: 'verify-elevator-relays', label: 'Elevator Relays', entry: 'checkbox' },
    ],
  },
  {
    id: 'install-cr202-photo',
    title: '2. Take a Picture of CR202',
    items: [{ id: 'cr202-photo', label: 'Picture of the CR202', entry: 'photo' }],
  },
  {
    id: 'install-robot-details',
    title: '3. Robot & CR202 Details',
    items: [
      {
        id: 'robot-serial-number',
        label: 'Log Robot Serial Number',
        entry: 'field',
        placeholder: 'e.g. L352507606050zA',
      },
      {
        id: 'robot-ip-address',
        label: 'Log Robot IP Address',
        entry: 'field',
        placeholder: 'e.g. 192.168.1.52 — Private',
      },
      {
        id: 'cr202-ip-address',
        label: 'Log IP Address of CR202 — What’s My IP',
        entry: 'field',
        placeholder: 'e.g. 192.168.2.1 — Public 63.42.52.119',
      },
    ],
  },
  {
    id: 'install-setup',
    title: '4. Inventory & Autoxing Set-Up',
    items: [
      { id: 'update-robot-inventory-sheet', label: 'Update Robot Inventory Spread Sheet', entry: 'checkbox' },
      { id: 'autoxing-setup-building', label: 'Autoxing — Set-Up Building', entry: 'checkbox' },
      { id: 'autoxing-setup-business', label: 'Autoxing — Set-Up Business', entry: 'checkbox' },
    ],
  },
  {
    id: 'install-mapping',
    title: '5. Mapping & Testing',
    items: [
      { id: 'create-map', label: 'Create Map', entry: 'checkbox' },
      { id: 'sync-map-on-robot', label: 'Save and Sync Map on Robot', entry: 'checkbox' },
      { id: 'set-points', label: 'Set Points — Central Points and Shelf Points', entry: 'checkbox' },
      { id: 'draw-track', label: 'Draw Track', entry: 'checkbox' },
      { id: 'create-zones', label: 'Create Zones', entry: 'checkbox' },
      { id: 'robot-testing', label: 'Robot Testing', entry: 'checkbox' },
    ],
  },
];

export type InstallationChecklistState = Record<string, string | boolean>;

/** Photo item ids double as the `section_id` of their attachment rows. */
export const INSTALLATION_CHECKLIST_PHOTO_ITEM_IDS: readonly string[] = INSTALLATION_CHECKLIST_SECTIONS.flatMap(
  (section) => section.items.filter((item) => item.entry === 'photo').map((item) => item.id)
);

/** How many photos each photo item currently has, keyed by item id. */
export type InstallationChecklistPhotoCounts = Record<string, number>;

export function installationChecklistItemLabel(itemId: string): string | null {
  for (const section of INSTALLATION_CHECKLIST_SECTIONS) {
    const item = section.items.find((i) => i.id === itemId);
    if (item) return item.label;
  }
  return null;
}

/**
 * Normalize API JSON into typed state.
 *
 * Answers under keys this checklist does not define — an appointment filled in
 * against the retired facility-readiness checklist — are carried through rather
 * than dropped. They are not rendered, but autosave writes the whole object back,
 * so dropping them here would erase what an installer actually recorded.
 */
export function normalizeInstallationChecklistState(raw: unknown): InstallationChecklistState {
  const rawObj = raw && typeof raw === 'object' && !Array.isArray(raw) ? (raw as Record<string, unknown>) : {};
  const out: InstallationChecklistState = {};
  for (const [key, value] of Object.entries(rawObj)) {
    if (typeof value === 'string' || typeof value === 'boolean') out[key] = value;
    else if (typeof value === 'number' && !Number.isNaN(value)) out[key] = String(value);
  }
  for (const section of INSTALLATION_CHECKLIST_SECTIONS) {
    for (const item of section.items) {
      // A photo item's answer is its attachment rows, so it holds no checklist state.
      if (item.entry === 'photo') continue;
      const v = rawObj[item.id];
      if (item.entry === 'field') {
        if (typeof v === 'string') out[item.id] = v;
        else if (typeof v === 'number' && !Number.isNaN(v)) out[item.id] = String(v);
        else out[item.id] = '';
      } else if (typeof v === 'boolean') {
        out[item.id] = v;
      } else if (typeof v === 'string') {
        const t = v.trim().toLowerCase();
        out[item.id] = t === 'true' || t === 'yes' || t === '1' || t === 'y';
      } else {
        out[item.id] = false;
      }
    }
  }
  return out;
}

/** A photo item counts as done once at least one picture is attached; its note is optional. */
export function isInstallationChecklistItemComplete(
  item: InstallationChecklistItem,
  state: InstallationChecklistState,
  photoCounts: InstallationChecklistPhotoCounts = {}
): boolean {
  if (item.entry === 'field') return String(state[item.id] ?? '').trim().length > 0;
  if (item.entry === 'photo') return (photoCounts[item.id] ?? 0) > 0;
  return state[item.id] === true;
}

export function isInstallationChecklistSectionComplete(
  section: InstallationChecklistSection,
  state: InstallationChecklistState,
  photoCounts: InstallationChecklistPhotoCounts = {}
): boolean {
  return section.items.every((item) => isInstallationChecklistItemComplete(item, state, photoCounts));
}
